/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Shared helper for fire-and-forget paths where failure must not block UX
 * but must be observable (CCP-665 / DEF-05 chain).
 */

import { captureMessage } from "@/lib/monitoring/sentry";
import { logger } from "@/lib/utils/logger";

export type IntentionalNonBlockingRejectionParams = {
  /** Stable handler name, e.g. CACHE_INVALIDATE_SERVICE_UPDATE */
  operation: string;
  /** Broad area: api, client_hook, time_entries, etc. */
  surface: string;
  err: unknown;
  organizationDbId?: string | null;
  userId?: string | null;
  requestId?: string | null;
  /** Optional entity id for correlation (serviceId, clientId, …) */
  entityId?: string | null;
  /** Optional short detail (e.g. batch size) — keep non-sensitive */
  detail?: string;
};

/**
 * Structured warn + Sentry capture for intentional non-blocking rejections.
 * Does not throw.
 */
export function logIntentionalNonBlockingRejection(
  params: IntentionalNonBlockingRejectionParams,
): void {
  const {
    operation,
    surface,
    err,
    organizationDbId,
    userId,
    requestId,
    entityId,
    detail,
  } = params;
  const message = `[${surface}] ${operation} rejected (non-blocking)`;
  logger.warn(message, {
    operation,
    surface,
    organizationDbId: organizationDbId ?? undefined,
    userId: userId ?? undefined,
    requestId: requestId ?? undefined,
    entityId: entityId ?? undefined,
    detail: detail ?? undefined,
    error: err instanceof Error ? err.message : String(err),
  });
  void captureMessage(message, {
    level: "warning",
    tags: {
      swallow: "intentional_non_blocking",
      operation,
      surface,
    },
    contexts: {
      org: { organizationDbId: organizationDbId ?? "unknown" },
      request: { requestId: requestId ?? "n/a", userId: userId ?? "n/a" },
      entity: { entityId: entityId ?? "n/a", detail: detail ?? "" },
    },
  });
}
